/**
 * src/api/ssoApi.js
 *
 * Frontend API for Entra ID SSO operations.
 */

import { getFunctionUrl } from './functionUrl.js';

/**
 * Fetches the SSO mode for a tenant (public, no auth required).
 * Used by the login screen to determine whether to show the SSO button.
 * @param {string} tenantId
 * @returns {{ mode: 'disabled' | 'optional' | 'enforced' }}
 */
export async function fetchSsoConfig(tenantId, baseUrl) {
  try {
    const ssoConfigUrl = baseUrl ? `${baseUrl}/api/sso-config` : getFunctionUrl('sso-config');
    const res = await fetch(`${ssoConfigUrl}?tenantId=${encodeURIComponent(tenantId)}`);
    if (!res.ok) return { mode: 'disabled' };
    return await res.json();
  } catch {
    return { mode: 'disabled' };
  }
}

/**
 * Initiates the SSO flow — calls entra-sso-init to get the authorization URL.
 * @param {string} tenantId
 * @returns {{ authUrl: string, state: string } | { error: string }}
 */
export async function initSso(tenantId, baseUrl) {
  const initUrl = baseUrl ? `${baseUrl}/api/entra-sso-init` : getFunctionUrl('entra-sso-init');
  const res = await fetch(initUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId }),
  });

  const data = await res.json();
  if (!res.ok) return { error: data.error || 'Failed to initiate SSO' };
  return data;
}

/**
 * Exchanges the authorization code for a SeaSuite JWT.
 * @param {{ code: string, state: string, tenantId: string, redirectUri: string }}
 * @returns {{ token: string, user: object } | { error: string }}
 */
export async function completeSso({ code, state, tenantId, redirectUri }) {
  const res = await fetch(getFunctionUrl('entra-sso-callback'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state, tenantId, redirectUri }),
  });

  const data = await res.json();
  if (!res.ok) return { error: data.error || data.message || 'SSO authentication failed' };
  return data;
}

/**
 * Redeems an emergency fallback token (generated by super-admin).
 * @param {{ tenantId: string, token: string }}
 * @returns {{ token: string, user: object } | { error: string }}
 */
export async function redeemFallbackToken({ tenantId, token }) {
  const res = await fetch(getFunctionUrl('entra-sso-fallback'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'redeem', tenantId, token }),
  });

  const data = await res.json();
  if (!res.ok) return { error: data.error || 'Failed to redeem emergency token' };
  return data;
}
